import { PlatformType } from "./Platform";

export interface Post {
  id: string;
  userId: string;
  workspaceId?: string;
  /** Auth UID for new user-authored posts; absent on legacy/system records. */
  createdByUserId?: string;
  brandKitId?: string;

  // Basic post info
  title?: string; // not in use from 4/28/2025
  description?: string;
  hashtags?: string[]; // separated for easier management
  firstComment?: string;
  scheduledFor?: number;

  // Media
  mediaItems: MediaItem[];

  // Publishing
  status:
    | "draft"
    | "scheduled"
    | "pre-processing"
    | "publishing"
    | "published"
    | "failed"
    | "mixed"
    | "generating";

  // Platform specific data
  connections: {
    [connectionId: string]: ConnectionPost;
  };

  // Timestamps
  createdAt: number;
  updatedAt: number;
  exportedAt?: number; // Timestamp of last export
  generationError?: string | null;
  /** Null marks a manually created post; missing is supported only for legacy data. */
  generationSource?: string | null;
  generationSourceLabel?: string;
  generationSourceType?: "url" | "text" | "youtube" | "pdf";
  generationSourceFavicon?: string;
  generationRequestId?: string;
  /** Stable identity of this source within its generation request. */
  generationSourceId?: string;
  /** Stable source-generation time retained by duplicates for shelf ordering. */
  generationSourceCreatedAt?: number;
  generationQualityVariant?:
    | "pre-aug5"
    | "aug5-bundle"
    | "explicit-style-control"
    | "explicit-style-anchor";
  generationStyleConsistency?: {
    visualStyleId: string;
    eligible: boolean;
    generatedImageCount: number;
    anchorApplied: boolean;
  };

  // Extracted article images (for "From article" image picker option)
  extractedArticleImages?: ExtractedArticleImage[];

  // Batch generation
  batchId?: string; // Groups posts from one batch run
  batchIndex?: number; // 0-based ordering within batch

  // Sharing
  shareId?: string; // Unique URL-safe ID for public sharing
  shareEnabled?: boolean; // Manual toggle to enable/disable sharing
  sharePreviewPath?: string; // Path to generated preview image with overlays

  // Claim fields (for unclaimed posts from website)
  claimToken?: string; // Secret token for claiming
  claimExpiresAt?: number; // Timestamp when claim expires

  // Shared video choreography, prepared during carousel generation and
  // retained when switching between image and video output.
  videoChoreography?: import('./ChoreographySpec').VideoChoreography;
  // Whether the choreography applies to the whole post ("this is a video
  // post" for platforms that support rendered video output). Missing = true
  // when choreography exists (legacy posts).
  // false = choreography is only a cached rendition for video output; the post stays a carousel elsewhere. Per-connection settings
  // can override via their own videoEnabled field.
  videoEnabled?: boolean;
  videoChoreographyHash?: string; // Structural hash at time of choreography generation
  videoMusicPath?: string; // Firebase Storage path to selected music track
  videoMusicName?: string; // Display name of selected music track
  videoAudioMuted?: boolean; // Whether to render carousel videos without audio
  videoAnimationStyle?: import('./ChoreographySpec').AnimationStyle;
  videoSpeed?: number;

  // Website carousel data (stored for unclaimed posts from website)
  websiteCarouselData?: {
    templateId: string;
    content: any; // CarouselContent from website
    platform: string;
    intent?: string;
  };
}

/**
 * Whether the post publishes as a rendered video for platforms that support
 * video output. A missing videoEnabled counts as true so legacy video posts
 * keep working; videoEnabled=false means the choreography is only a cached
 * rendition available to any video-capable platform.
 */
export function isVideoPost(
  post: Pick<Post, "videoChoreography" | "videoEnabled">
): boolean {
  return !!post.videoChoreography && post.videoEnabled !== false;
}

/**
 * Represents an image extracted from the source article
 * Used for the "From article" option in the image picker
 */
export interface ExtractedArticleImage {
  /** Firebase Storage path */
  storagePath: string;
  /** Alt text from the image tag */
  altText?: string;
  /** Surrounding context (nearby text/caption) */
  context?: string;
  /** Original dimensions */
  dimensions: {
    width: number;
    height: number;
  };
  /** Position in article (0 = first image found) */
  position: number;
}

export interface MediaItem {
  path: string; // e.g. "uploads/abc123.mp4"
  type: "video" | "image";
  metadata: {
    duration?: number; // in seconds
    dimensions: {
      width: number;
      height: number;
    };
    size: number; // in bytes
    thumbnailPath?: string;
    coverTimestamp?: number; // timestamp in milliseconds for video cover
    isCustomCover?: boolean; // indicates if cover was explicitly set by user
    coverViewMode?: "contain" | "cover"; // view mode for the cover image
    textOverlays?: TextOverlay[]; // Legacy field for backward compatibility
    overlays?: AnyOverlay[]; // Replaces textOverlays to support multiple overlay types
    backgroundColor?: string; // Original fill color when background was generated from a solid color (hex)
    isBackgroundTemplateAsset?: boolean; // When true, background image is a static template asset
    backgroundFilter?: ImageFilter; // Visual filter applied to the background image at render time

    // Audio settings for video media items
    audioSettings?: AudioSettings;
    outroSettings?: OutroSettings; // NEW: Settings for the optional outro screen

    // --- New Cover Settings ---
    coverSettings?: CoverSettings;

    // --- Watermark flag ---
    generatedWithWatermark?: boolean; // Whether this content was generated with watermark (for audit trail)

    // --- Layout engine version ---
    // Determines how overlays are rendered to handle CSS vs Canvas differences.
    //
    // Values:
    //   - undefined/'fabric': Old posts created with Fabric.js editor
    //     → Apply CSS compensations (e.g., half-leading offset for text positioning)
    //   - 'remotion': New posts created with Remotion editor
    //     → Use clean CSS behavior, no compensations needed
    //
    // Migration strategy:
    //   - Only set 'remotion' when creating BRAND NEW posts
    //   - When editing existing posts, preserve the original layoutEngine value
    //   - This ensures old posts render identically even after edits
    //   - Technical debt naturally phases out as old posts age out
    //
    // Why this matters:
    //   - Fabric.js (canvas) positions text from the top of the bounding box
    //   - CSS adds "half-leading" above the first line based on line-height
    //   - For line-height 1.4, text appears ~0.2 * fontSize lower in CSS
    //   - The compensation adjusts the top position to match Fabric.js rendering
    layoutEngine?: 'fabric' | 'remotion';
  };
}

// Define the style properties separately
export interface TextOverlayStyle {
  fontFamily: string; // Font family
  fontSize: number; // Font size
  color: string; // Text color (hex)
  backgroundColor: string; // Background color (hex)
  opacity: number; // Background opacity (0-100%)
  textAlign: "left" | "center" | "right";
  fontWeight: "normal" | "bold";
  fontStyle: "normal" | "italic";
  textDecoration: "none" | "underline";
  textTransform?: "none" | "uppercase"; // Display-time letter case; the stored text keeps its typed casing
  lineHeight?: number; // Add optional line height (unitless multiplier)
  borderRadius?: number; // Optional border radius for the background
  useSvgFilter?: boolean;
  strokeWidthRatio?: number; // Optional: Stroke width as ratio of font size (e.g., 0.05 for 5%)
  strokeColor?: string; // Optional: Stroke color (hex)
  textShadow?: string; // Optional: Text shadow (e.g., "2px 2px 4px rgba(0,0,0,0.5)")
  padding?: {
    // All values in pixels, applied to the background/block
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  wrappingWidthPx?: number;
}

// --- Rich text runs ---
// Per-run style overrides, merged over the overlay's base TextOverlayStyle.
// Deliberately a whitelist: element-level fields (textAlign, padding,
// wrappingWidthPx, lineHeight, useSvgFilter) are never per-run. Note the
// reinterpretation: opacity and borderRadius here configure the run's
// HIGHLIGHT PILL, mirroring how the base fields configure the block background.
export interface TextRunStyle {
  fontFamily?: string;
  fontSize?: number; // Same unit/space as TextOverlayStyle.fontSize (unscaled)
  color?: string;
  highlightColor?: string; // Inline background pill behind this run (distinct from the block backgroundColor)
  opacity?: number; // Highlight pill opacity 0-100 (default 100)
  borderRadius?: number; // Highlight pill radius in px (default: small em-based radius)
  fontWeight?: "normal" | "bold";
  fontStyle?: "normal" | "italic";
  textDecoration?: "none" | "underline";
  textTransform?: "none" | "uppercase"; // "none" explicitly blocks an inherited base "uppercase"
  strokeWidthRatio?: number; // Text stroke, same semantics as the base style
  strokeColor?: string;
  textShadow?: string; // CSS shadow; "none" explicitly blocks the inherited base shadow
}

export interface TextRun {
  text: string;
  style?: TextRunStyle;
}

export function textRunsText(runs: TextRun[]): string {
  return runs.map((r) => r.text).join("");
}

/**
 * Returns the overlay's runs only when they exactly reconstruct `text` and at
 * least one run carries styling. Any mismatch means the runs are stale, so
 * renderers must fall back to the uniform-style path rather than guess.
 */
export function getRenderableRuns(
  overlay: Pick<TextOverlay, "text" | "runs">
): TextRun[] | null {
  const runs = overlay.runs;
  if (!runs || runs.length === 0) return null;
  if (textRunsText(runs) !== overlay.text) return null;
  if (!runs.some((r) => r.style && Object.keys(r.style).length > 0)) return null;
  return runs;
}

const RUN_STYLE_KEYS: (keyof TextRunStyle)[] = [
  "fontFamily",
  "fontSize",
  "color",
  "highlightColor",
  "opacity",
  "borderRadius",
  "fontWeight",
  "fontStyle",
  "textDecoration",
  "textTransform",
  "strokeWidthRatio",
  "strokeColor",
  "textShadow",
];

export function textRunStylesEqual(a?: TextRunStyle, b?: TextRunStyle): boolean {
  const aEmpty = !a || Object.keys(a).length === 0;
  const bEmpty = !b || Object.keys(b).length === 0;
  if (aEmpty || bEmpty) return aEmpty === bEmpty;
  return RUN_STYLE_KEYS.every((k) => a![k] === b![k]);
}

/**
 * Canonical form: drops empty runs, strips empty style objects, merges
 * adjacent runs with equal style. Returns undefined when nothing is styled —
 * the overlay should then carry no `runs` at all.
 */
export function normalizeTextRuns(runs: TextRun[]): TextRun[] | undefined {
  const merged: TextRun[] = [];
  for (const run of runs) {
    if (run.text.length === 0) continue;
    const style = run.style && Object.keys(run.style).length > 0 ? run.style : undefined;
    const prev = merged[merged.length - 1];
    if (prev && textRunStylesEqual(prev.style, style)) {
      merged[merged.length - 1] = { ...prev, text: prev.text + run.text };
    } else {
      merged.push(style ? { text: run.text, style } : { text: run.text });
    }
  }
  return merged.some((r) => r.style) ? merged : undefined;
}

/**
 * Split runs into paragraphs on double newlines, preserving each fragment's
 * style. Mirrors the `text.split(/\n\n+/)` paragraph handling of the per-line
 * background mode. A paragraph break split across two runs (one "\n" ending a
 * run, another starting the next) is not detected; styled runs must not
 * contain newlines, so this only affects hand-crafted data.
 */
export function splitRunsIntoParagraphs(runs: TextRun[]): TextRun[][] {
  const paragraphs: TextRun[][] = [[]];
  for (const run of runs) {
    const parts = run.text.split(/\n\n+/);
    parts.forEach((part, i) => {
      if (i > 0) paragraphs.push([]);
      if (part.length > 0) {
        paragraphs[paragraphs.length - 1].push({ ...run, text: part });
      }
    });
  }
  return paragraphs;
}

// Animation interface for overlay animations
export interface OverlayAnimation {
  type: "fadeIn" | "slideIn" | "wipe";
  duration?: number; // Duration in seconds, defaults to 1.0
  easing?: "linear" | "easeOut" | "easeIn" | "easeInOut"; // Defaults to "easeOut"
  direction?: "left" | "right" | "top" | "bottom"; // For slideIn animations
}

export type TextOverlay = {
  id: string; // Unique identifier
  type?: "text"; // Made optional for backward compatibility
  text: string; // The text content
  runs?: TextRun[]; // Optional styled segments partitioning `text`; concat of run texts must equal `text` (see getRenderableRuns)
  style: TextOverlayStyle; // Use the defined TextOverlayStyle type
  position: {
    x: number; // Percentage from left
    y: number; // Percentage from top
  };
  rotation: number; // Rotation in degrees
  width?: number; // Optional width as percentage of container
  height?: number; // Add optional height
  zIndex: number; // For stacking order of multiple overlays
  timing?: {
    startTime: number; // Start time in seconds
    endTime: number; // End time in seconds
  };
  animation?: OverlayAnimation; // Optional animation configuration
  imageDataUrl?: string; // Optional: Base64 PNG data URL of the captured overlay
  imageStoragePath?: string; // New: Firebase Storage path (e.g., posts/userId/draftId/overlays/overlayId-timestamp.png)
};

// Shadow configuration for overlays
export interface Shadow {
  blur: number; // Blur radius in pixels
  color: string; // Color with alpha, e.g., "rgba(0,0,0,0.3)"
  offsetX?: number; // Horizontal offset in pixels (default: 0)
  offsetY?: number; // Vertical offset in pixels (default: 0)
}

// Visual filter presets that can be applied to an image at render time.
// Mapped to CSS `filter` by the renderer. Kept as no-param presets for now.
export type ImageFilter = "grayscale" | "sepia" | "invert";

// --- NEW: ImageOverlay Interface ---
export interface ImageOverlay {
  id: string;
  type: "image";
  filePath: string; // Changed from imageUrl to reflect it's a storage path
  position: { x: number; y: number };
  width: number; // Percentage of video/container width
  height?: number; // Optional: Percentage of video/container height. If not set, aspect ratio is preserved based on width.
  opacity: number; // NOW: 0-100 to match component state. Will be converted to 0-1 on server.
  zIndex?: number;
  timing?: { startTime: number; endTime: number };
  animation?: OverlayAnimation; // Optional animation configuration
  rotation?: number;
  borderRadius?: number;
  objectFit?: "fill" | "contain" | "cover"; // How image should fit within dimensions (default: "cover")
  objectPosition?: { x: number; y: number }; // Crop focal point for cover mode, as percentages (default: 50/50)
  shadow?: Shadow; // Optional drop shadow
  sourceColor?: string; // Original fill color when overlay was generated from a solid color (hex)
  isTemplateAsset?: boolean; // When true, this image is a static template asset (not dynamic content)
  filter?: ImageFilter; // Visual filter applied at render time (preserved across image swaps)
}

// --- Gradient Configuration ---
export interface GradientColorStop {
  offset: number; // 0-1, position of the color stop
  color: string; // Hex color, e.g., "#FF0000"
  opacity?: number; // 0-1, optional per-stop alpha (defaults to 1 if omitted)
}

export interface GradientConfig {
  type: "linear" | "radial";
  angle?: number; // For linear gradients, angle in degrees (0-360). 0 = left to right, 90 = top to bottom
  colorStops: GradientColorStop[]; // At least 2 color stops
}

// Helper to check if a fill is a gradient
export function isGradientConfig(fill: string | GradientConfig | undefined): fill is GradientConfig {
  return typeof fill === "object" && fill !== null && "type" in fill && "colorStops" in fill;
}

// --- NEW: SvgOverlay Interface ---
export interface SvgOverlay {
  id: string;
  type: "svg";
  svgContent: string; // Raw SVG inner content (without <svg> wrapper)
  viewBox: string; // SVG viewBox, e.g., "0 0 100 100"
  position: { x: number; y: number }; // Percentage from left/top
  width: number; // Percentage of video/container width
  height?: number; // Optional: Percentage of video/container height. If not set, aspect ratio is preserved.
  opacity: number; // 0-100 to match component state
  zIndex?: number;
  timing?: { startTime: number; endTime: number };
  animation?: OverlayAnimation; // Optional animation configuration
  rotation?: number;

  // SVG-specific properties for user color customization (frontend only)
  // These override the baked-in colors in svgContent when set
  fillColor?: string | GradientConfig; // Override fill color (hex string or gradient config)
  strokeColor?: string; // Override stroke color (hex)
  strokeWidth?: number; // Override stroke width in pixels

  // Pre-rendered PNG for server processing (like TextOverlay)
  imageStoragePath?: string; // Firebase Storage path to rendered PNG
}

// --- NEW: Union type for any kind of overlay ---
export type AnyOverlay = TextOverlay | ImageOverlay | SvgOverlay;

// --- NEW: AudioSettings Interface ---
export interface AudioSettings {
  audioPath?: string; // Firebase Storage path to the audio file
  volume?: number; // 0-100
  startTime?: number; // In seconds
  endTime?: number; // In seconds
  videoVolume?: number; // 0-100 - volume for the original video
  loop?: boolean; // Whether to loop the audio
}

export interface OutroSettings {
  enabled: boolean;
  backgroundType: "color" | "image";
  backgroundColor?: string;
  backgroundImagePath?: string;
  durationSeconds: number;
  overlays?: AnyOverlay[];
}

export interface CoverSettings {
  isCustom: boolean;
  sourceImagePath?: string;
  overlays?: AnyOverlay[];
  viewMode?: "contain" | "cover";
}

// Per-connection overrides of the post-level video fields, following the
// same mirroring convention as description/title: effective value at publish
// is settings.X ?? post.X. They shape the video this connection publishes —
// whether the post is a video post or a carousel converted per-connection.
export type VideoSettingsOverrides = {
  // Missing = post-level state, else platform default (true for YouTube:
  // carousels publish as a video slideshow)
  videoEnabled?: boolean;
  videoMusicPath?: string;
  videoMusicName?: string;
  videoAudioMuted?: boolean;
  videoSpeed?: number;
};

// Per-connection first comment. Enabled is remembered per channel (see
// publishDefaults); missing = legacy post, where a post-level firstComment
// means "post it on every platform that supports comments". Text missing =
// use post.firstComment.
export type FirstCommentOverrides = {
  firstCommentEnabled?: boolean;
  firstComment?: string;
};

export type TiktokSettings = VideoSettingsOverrides & {
  privacy?: string;
  commercialContentEnabled: boolean;
  yourBrand: boolean;
  brandedContent: boolean;
  allowComments: boolean;
  allowDuet: boolean;
  allowStitch: boolean;
  aiGeneratedContent: boolean;
  description?: string;
  title?: string;
};

export type YoutubeSettings = VideoSettingsOverrides & FirstCommentOverrides & {
  privacy?: "public" | "unlisted" | "private";
  // playlist?: string;
  madeForKids: boolean;
  // allowComments: boolean;
  description?: string;
  title?: string;
};

export type InstagramSettings = VideoSettingsOverrides & FirstCommentOverrides & {
  // location?: string;
  // hidePostStats: boolean;
  // turnOffComments: boolean;
  postType?: "feed" | "story"; // Determines if post should be published as a regular post or story
  description?: string;
};

export type LinkedinSettings = VideoSettingsOverrides & FirstCommentOverrides & {
  title?: string;
  description?: string;
};

export type FacebookSettings = VideoSettingsOverrides & FirstCommentOverrides & {
  description?: string;
  postType?: "feed" | "story";
};

export type PinterestSettings = {
  title?: string;
  description?: string;
  descriptionSource?: "generated" | "manual";
  boardId?: string;
  boardName?: string;
  link?: string;
};

export interface ProcessedMediaItem {
  type: "video" | "image";
  processedPath: string;
}

export type PlatformSpecificSettings = {
  tiktok: TiktokSettings;
  youtube: YoutubeSettings;
  instagram: InstagramSettings;
  linkedin: LinkedinSettings;
  facebook: FacebookSettings;
  pinterest: PinterestSettings;
};

export interface ConnectionPost {
  connectionId: string; // References PlatformConnection
  platform: PlatformType;
  status: "pending" | "pre-processing" | "publishing" | "published" | "failed";
  settings?: PlatformSpecificSettings[keyof PlatformSpecificSettings];
  postId?: string; // Platform's post ID after publishing
  storyIds?: string[]; // IDs of published stories (for carousel stories)
  // Carousel published as separate stories: what's already live. Kept after a
  // failed attempt so a retry continues from the first unpublished slide;
  // cleared once every slide is published.
  storyProgress?: {
    slides: string[]; // fingerprint of each published slide, in order
    storyIds: string[];
    total: number;
  };
  publishedAt?: number;
  contentType?: string; // Platform-specific content types, e.g. reel vs story, etc
  taskName?: string; // Cloud Tasks task name for scheduled posts
  deferred?: boolean; // true = scheduled >29 days out, Cloud Task will be created later by cron
  processedMedia?: ProcessedMediaItem;

  // How postId was obtained. TikTok sometimes only yields an internal
  // publish_id (not queryable against video endpoints) when the public id
  // isn't available before status polling ends.
  postIdType?: "platform" | "publish_id";

  // TikTok publish state retained while asynchronous platform processing is
  // still in progress. Automatic retries resume this publish operation.
  pendingPublishId?: string;
  pendingPublishStartedAt?: number;
  pendingPublishInitializationId?: string;
  pendingPublishInitializationStartedAt?: number;
  publishAttemptStartedAt?: number;

  // Engagement metrics live in the post_analytics collection (one doc per
  // post × connection), not on the post document. See models/PostAnalytics.ts.
  error?: {
    code: string;
    message: string;
    timestamp: number;
    retryCount: number;
    retryable?: boolean;
    cancelled?: boolean;
  };

  firstCommentResult?: {
    status: "posted" | "failed";
    commentId?: string;
    error?: string;
  };

  // Per-connection schedule time; takes precedence over post.scheduledFor
  scheduledFor?: number;
}

export interface PostFeedback {
  id: string; // Unique ID for the feedback document itself
  postId: string; // ID of the Post this feedback relates to
  workspaceId: string; // Workspace that owns the post and authorizes access
  userId: string; // ID of the user who submitted the feedback
  rating: "up" | "down"; // The thumbs up/down rating
  comment?: string; // Optional detailed textual feedback
  timestamp: number; // Timestamp of when the feedback was submitted
}
